import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';

import { AnimatedViews, useResponsive, VStack, useToggleState } from '@garnish/core';
import { AddressTypes } from '@garnish/location';
import { addHours } from 'date-fns/addHours';
import { isAfter } from 'date-fns/isAfter';

import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import {
  OrderStatus,
  useHasEarnedLoyaltyPoints,
  useOrderStatusData,
  useLateOrderNoticeBanner,
} from '@order/features-order-status';
import { useContactUsHandler } from '@order/shared-hooks';
import { ignoreTimezone } from '@order/utils';

import {
  AppFooter,
  ChallengeCompletionModal,
  NotFoundView,
  SignedOutView,
  OrderCancellationModal,
  useOrderCancellationModal,
} from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useCustomer } from '../../context/Customer';
import { useDeliveryOrderInFlight, useOrderStatusProgressStepIcons } from '../../hooks';
import { useReorderFromOrder } from '../ReorderingScreen';
import { OrderStatusLineItems } from './components';

import type { AddressType } from '@garnish/location';

export const OrderStatusScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) return <OrderStatus.Loading />;

  if (!isLoggedIn) return <SignedOutView />;

  return <OrderStatusScreenSignedIn />;
};

const OrderStatusScreenSignedIn = () => {
  const isLoggedIn = useIsLoggedIn();
  const { customer } = useCustomer();

  const data = useOrderStatusData({ isLoggedIn, customer });

  const onReorder = useReorderFromOrder(data.order);

  useDeliveryOrderInFlight(); // results later used from cache.

  const { isRenderedOnScreen, isFetching, isInvalidOrderStatus, celebration } = data;

  if (isInvalidOrderStatus) return <NotFoundView />;

  if (isFetching) return <OrderStatus.Loading />;

  return (
    <>
      <AnimatedViews.Reveal style={styles.container}>
        <OrderStatusScreenReady {...data} onReorder={onReorder} />
      </AnimatedViews.Reveal>

      {celebration && isRenderedOnScreen ? (
        <ChallengeCompletionModal challengeCompletionDetails={celebration} />
      ) : null}
    </>
  );
};

const OrderStatusScreenReady = (
  props: ReturnType<typeof useOrderStatusData> & { readonly onReorder: () => void },
) => {
  const {
    reorderOrder,
    hasDeliveryAddress,
    deliveryAddressName,
    deliveryAddressStreet,
    deliveryAddressSecondaryStreet,
    deliveryAddressCity,
    deliveryAddressState,
    deliveryAddressZipCode,
    deliveryAddressNotes,
    deliveryAddressType,
    deliveryAddressLatitude,
    deliveryAddressLongitude,
    deliveryEstimatedTime,
    deliveryPreference,
    locationName,
    locationAddress,
    locationCity,
    locationState,
    locationZipCode,
    locationLatitude,
    locationLongitude,
    dropoffLocation,
    orderId,
    orderType,
    orderStatus,
    orderTotal,
    orderWantedTime,
    orderLedger,
    orderLineItems,
    isGroupOrder,
    payments,
    isLate,
    isPast,
    isPendingFeedback = false,
    isTrackable = false,
    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,
    vendorId,
    canCancel,
    customerName,
    customerLatitude,
    customerLongitude,
    courierName,
    courierPhone,
    courierLatitude,
    courierLongitude,
    restaurantId,
    onReorder,
    refetchOrderStatus,
  } = props;

  const router = useRouter();
  const { currentBreakpoint } = useResponsive();

  const { handleContactUs } = useContactUsHandler('order-status');
  const progressStepIconsByChannel = useOrderStatusProgressStepIcons();

  const isInStoreOrder = orderType === AddressTypes['in-store'];
  const isOrderCancelled = orderStatus === 'failed';
  const canReorder = !isGroupOrder && !isInStoreOrder && (isOrderCancelled || isPast);

  const canRateOrder = !isOrderCancelled && isPast && isPendingFeedback;

  const hasEarnedLoyaltyPoints = useHasEarnedLoyaltyPoints(true, orderId, isOrderCancelled);
  const shouldShowLoyaltyPointsBanner = checkIfLoyaltyPointsBannerIsVisible({
    hasEarnedLoyaltyPoints,
    orderCompletedAt: orderWantedTime,
  });

  const shouldShowCourierInfo =
    orderType === 'delivery' &&
    orderStatus === FlattenedOrderStatuses.Delivering &&
    courierName &&
    courierPhone;

  const shouldShowAddress = orderType !== 'delivery' || hasDeliveryAddress;

  const shouldShowPayments = payments.length > 0;

  const shouldRenderOrderToHealthKit =
    isOrderToHealthKitEnabled &&
    orderToHealthKitLineItems?.length !== undefined &&
    orderWantedTime !== undefined &&
    Platform.OS === 'ios';

  const shouldDisplayInAppCancellationModal =
    canCancel && orderId !== undefined && restaurantId !== undefined;

  const progressStepIcons = getProgressStepIcons({ progressStepIconsByChannel, orderType });

  useLateOrderNoticeBanner({ isLate });

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: shouldHideCancelDuringAsyncCancelation,
    toggleOn: hideCancelDuringAsyncCancelation,
  } = useToggleState();

  const {
    isOrderCancellationModalVisible,
    showOrderCancellationModal,
    hideOrderCancellationModal,
  } = useOrderCancellationModal();

  // ─── Callbacks ───────────────────────────────────────────────────────────────────

  const handleNavigateToRateOrderScreen = useCallback(() => {
    router.navigate({
      pathname: '/feedback/[orderId]',
      params: { orderId, isInStoreOrder: String(isInStoreOrder) },
    });
  }, [router, orderId, isInStoreOrder]);

  // ─────────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <>
        <OrderStatus.Layout.ContainerWrapper>
          <OrderStatus.Layout.Container>
            <OrderStatus.Layout.Info>
              <OrderStatus.Info
                orderId={orderId}
                vendorId={vendorId}
                orderType={orderType}
                orderStatus={orderStatus}
                wantedTime={orderWantedTime}
                estimatedDeliveryTime={deliveryEstimatedTime}
                orderTotal={orderTotal}
                locationName={locationName}
                locationAddress={locationAddress}
                isTrackable={isTrackable}
                canRateOrder={canRateOrder}
                canReorder={canReorder}
                isPast={isPast}
                progressStepIcons={progressStepIcons}
                isGroupOrder={isGroupOrder}
                onReorder={onReorder}
              />
            </OrderStatus.Layout.Info>

            <OrderStatus.Layout.Map>
              <OrderStatus.Map
                orderType={orderType}
                orderStatus={orderStatus}
                locationName={locationName}
                locationLatitude={locationLatitude}
                locationLongitude={locationLongitude}
                customerName={customerName}
                customerLatitude={customerLatitude}
                customerLongitude={customerLongitude}
                hasDeliveryAddress={hasDeliveryAddress}
                deliveryAddressName={deliveryAddressName}
                deliveryAddressType={deliveryAddressType}
                deliveryAddressLatitude={deliveryAddressLatitude}
                deliveryAddressLongitude={deliveryAddressLongitude}
                courierName={courierName}
                courierLatitude={courierLatitude}
                courierLongitude={courierLongitude}
              />
            </OrderStatus.Layout.Map>

            <OrderStatus.Layout.Content>
              <VStack gap={0} divider={<OrderStatus.Divider />}>
                {shouldShowLoyaltyPointsBanner ? <OrderStatus.LoyaltyPointsBanner /> : null}

                {shouldShowCourierInfo ? (
                  <OrderStatus.CourierInfo
                    orderType={orderType}
                    orderStatus={orderStatus}
                    courierName={courierName}
                    courierPhone={courierPhone}
                  />
                ) : null}

                {shouldShowAddress ? (
                  <OrderStatus.Address
                    orderType={orderType}
                    deliveryAddressName={deliveryAddressName}
                    deliveryAddressStreet={deliveryAddressStreet}
                    deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
                    deliveryAddressCity={deliveryAddressCity}
                    deliveryAddressState={deliveryAddressState}
                    deliveryAddressZipCode={deliveryAddressZipCode}
                    deliveryAddressNotes={deliveryAddressNotes}
                    deliveryPreference={deliveryPreference}
                    locationName={locationName}
                    locationCity={locationCity}
                    locationState={locationState}
                    locationZipCode={locationZipCode}
                    locationAddress={locationAddress}
                    dropoffLocation={dropoffLocation}
                  />
                ) : null}

                <View>
                  <OrderStatusLineItems
                    order={reorderOrder}
                    lineItems={orderLineItems}
                    isGroupOrder={isGroupOrder}
                  />

                  <OrderStatus.Ledger ledger={orderLedger} />
                </View>

                {shouldShowPayments ? <OrderStatus.Payments payments={payments} /> : null}

                {shouldRenderOrderToHealthKit ? (
                  <OrderStatus.OrderToHealthKit
                    lineItems={orderToHealthKitLineItems}
                    orderWantedTime={ignoreTimezone(orderWantedTime)}
                  />
                ) : null}
              </VStack>

              <OrderStatus.ContactUs
                orderId={orderId}
                canCancel={canCancel}
                restaurantId={restaurantId}
                onContactUs={handleContactUs}
                onCancelOrder={showOrderCancellationModal}
                shouldHideCancelOption={shouldHideCancelDuringAsyncCancelation}
              />

              {shouldDisplayInAppCancellationModal ? (
                <OrderCancellationModal
                  isVisible={isOrderCancellationModalVisible}
                  orderId={orderId}
                  restaurantId={restaurantId}
                  onSuccess={refetchOrderStatus}
                  onAsyncCancelation={hideCancelDuringAsyncCancelation}
                  onRequestClose={hideOrderCancellationModal}
                />
              ) : null}
            </OrderStatus.Layout.Content>
          </OrderStatus.Layout.Container>
        </OrderStatus.Layout.ContainerWrapper>

        {canReorder || canRateOrder ? (
          <OrderStatus.Layout.Footer>
            <OrderStatus.Ctas
              orderId={orderId}
              orderTotal={orderTotal}
              canRateOrder={canRateOrder}
              canReorder={canReorder}
              onReorder={onReorder}
              onRateOrder={handleNavigateToRateOrderScreen}
            />
          </OrderStatus.Layout.Footer>
        ) : null}
      </>
    );
  }

  return (
    <OrderStatus.Layout.ContainerWrapper>
      <OrderStatus.Layout.Container>
        <OrderStatus.Layout.Map>
          <OrderStatus.Map
            orderType={orderType}
            orderStatus={orderStatus}
            locationName={locationName}
            locationLatitude={locationLatitude}
            locationLongitude={locationLongitude}
            customerName={customerName}
            customerLatitude={customerLatitude}
            customerLongitude={customerLongitude}
            hasDeliveryAddress={hasDeliveryAddress}
            deliveryAddressName={deliveryAddressName}
            deliveryAddressType={deliveryAddressType}
            deliveryAddressLatitude={deliveryAddressLatitude}
            deliveryAddressLongitude={deliveryAddressLongitude}
            courierName={courierName}
            courierLatitude={courierLatitude}
            courierLongitude={courierLongitude}
          />
        </OrderStatus.Layout.Map>

        <OrderStatus.Layout.SidePanel>
          <OrderStatus.Layout.Info>
            <OrderStatus.Info
              orderId={orderId}
              vendorId={vendorId}
              orderType={orderType}
              orderStatus={orderStatus}
              wantedTime={orderWantedTime}
              estimatedDeliveryTime={deliveryEstimatedTime}
              orderTotal={orderTotal}
              locationName={locationName}
              locationAddress={locationAddress}
              isTrackable={isTrackable}
              canRateOrder={canRateOrder}
              canReorder={canReorder}
              isPast={isPast}
              progressStepIcons={progressStepIcons}
              isGroupOrder={isGroupOrder}
              onReorder={onReorder}
              onRateOrder={handleNavigateToRateOrderScreen}
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Content>
            <VStack gap={0} divider={<OrderStatus.Divider />}>
              {shouldShowLoyaltyPointsBanner ? <OrderStatus.LoyaltyPointsBanner /> : null}

              {shouldShowCourierInfo ? (
                <OrderStatus.CourierInfo
                  orderType={orderType}
                  orderStatus={orderStatus}
                  courierName={courierName}
                  courierPhone={courierPhone}
                />
              ) : null}

              {shouldShowAddress ? (
                <OrderStatus.Address
                  orderType={orderType}
                  deliveryAddressName={deliveryAddressName}
                  deliveryAddressStreet={deliveryAddressStreet}
                  deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
                  deliveryAddressCity={deliveryAddressCity}
                  deliveryAddressState={deliveryAddressState}
                  deliveryAddressZipCode={deliveryAddressZipCode}
                  deliveryAddressNotes={deliveryAddressNotes}
                  deliveryPreference={deliveryPreference}
                  locationName={locationName}
                  locationCity={locationCity}
                  locationState={locationState}
                  locationZipCode={locationZipCode}
                  locationAddress={locationAddress}
                  dropoffLocation={dropoffLocation}
                />
              ) : null}

              <View>
                <OrderStatusLineItems
                  order={reorderOrder}
                  lineItems={orderLineItems}
                  isGroupOrder={isGroupOrder}
                />

                <OrderStatus.Ledger ledger={orderLedger} />
              </View>

              {shouldShowPayments ? <OrderStatus.Payments payments={payments} /> : null}

              {shouldRenderOrderToHealthKit ? (
                <OrderStatus.OrderToHealthKit
                  lineItems={orderToHealthKitLineItems}
                  orderWantedTime={ignoreTimezone(orderWantedTime)}
                />
              ) : null}
            </VStack>

            <OrderStatus.ContactUs
              orderId={orderId}
              canCancel={canCancel}
              restaurantId={restaurantId}
              onContactUs={handleContactUs}
              onCancelOrder={showOrderCancellationModal}
              shouldHideCancelOption={shouldHideCancelDuringAsyncCancelation}
            />

            {shouldDisplayInAppCancellationModal ? (
              <OrderCancellationModal
                isVisible={isOrderCancellationModalVisible}
                orderId={orderId}
                restaurantId={restaurantId}
                onSuccess={refetchOrderStatus}
                onAsyncCancelation={hideCancelDuringAsyncCancelation}
                onRequestClose={hideOrderCancellationModal}
              />
            ) : null}
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.SidePanel>
      </OrderStatus.Layout.Container>

      <AppFooter />
    </OrderStatus.Layout.ContainerWrapper>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfLoyaltyPointsBannerIsVisible(params: LoyaltyPointsBannerVisibilityParams) {
  const { hasEarnedLoyaltyPoints, orderCompletedAt } = params;

  if (!hasEarnedLoyaltyPoints) return false;

  const completedAt = orderCompletedAt ? new Date(orderCompletedAt) : undefined;
  const expiresAt = completedAt ? addHours(completedAt, 24) : undefined;

  return expiresAt ? isAfter(expiresAt, new Date()) : true;
}

/**
 * Only pickup, outpost and delivery orders have progress step icons.
 */
function getProgressStepIcons(params: GetProgressStepIconsParams) {
  const { progressStepIconsByChannel, orderType } = params;

  switch (orderType) {
    case 'pickup':
    case 'outpost':
    case 'delivery': {
      return progressStepIconsByChannel[orderType];
    }

    default: {
      return [];
    }
  }
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LoyaltyPointsBannerVisibilityParams = {
  hasEarnedLoyaltyPoints: boolean;
  orderCompletedAt?: string;
};

type GetProgressStepIconsParams = {
  progressStepIconsByChannel: ReturnType<typeof useOrderStatusProgressStepIcons>;
  orderType: AddressType | undefined;
};
